import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Alert,
  Typography,
  Divider
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { normalizeGeotagMetadata } from '../../utils/geotagging';

const toNumberOrEmpty = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (Number.isNaN(Number(value))) return '';
  return String(Number(value));
};

const splitKeywords = (input) => {
  if (!input) return [];
  return input
    .split(/[,|\n]/)
    .map((keyword) => keyword.trim())
    .filter(Boolean);
};

const keywordListToInput = (keywords = []) => keywords.join(', ');

const GeotagDialog = ({
  open,
  onClose,
  onApply,
  defaults = {},
  initialMetadata = {},
  onGenerateDescription,
  generatingDescription = false,
  autoApply = false  // NEW: Enable auto-apply mode
}) => {
  const normalizedDefaults = useMemo(() => normalizeGeotagMetadata(defaults), [defaults]);
  const normalizedInitial = useMemo(() => normalizeGeotagMetadata(initialMetadata), [initialMetadata]);

  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [keywordsInput, setKeywordsInput] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [autoApplying, setAutoApplying] = useState(false);

  useEffect(() => {
    if (!open) return;

    const source = normalizedInitial.latitude != null && normalizedInitial.longitude != null
      ? normalizedInitial
      : normalizedDefaults;

    setLatitude(toNumberOrEmpty(source.latitude));
    setLongitude(toNumberOrEmpty(source.longitude));
    setKeywordsInput(keywordListToInput(
      normalizedInitial.keywords?.length ? normalizedInitial.keywords : normalizedDefaults.keywords
    ));
    setDescription(normalizedInitial.description || normalizedDefaults.description || '');
    setError('');
    setAutoApplying(false);
  }, [open, normalizedDefaults, normalizedInitial]);

  // Auto-apply geotag when dialog opens if all required fields are available
  useEffect(() => {
    if (!open || !autoApply || autoApplying) return;

    const source = normalizedInitial.latitude != null && normalizedInitial.longitude != null
      ? normalizedInitial
      : normalizedDefaults;

    // Check if all required fields are available
    const hasLatLng = source.latitude != null && source.longitude != null;
    const hasKeywords = source.keywords && source.keywords.length > 0;
    const hasDescription = source.description && source.description.trim().length > 0;

    if (hasLatLng && hasKeywords && hasDescription) {
      setAutoApplying(true);
      
      // Auto-apply with a small delay to show the form
      setTimeout(() => {
        const payload = normalizeGeotagMetadata({
          latitude: source.latitude,
          longitude: source.longitude,
          keywords: source.keywords,
          description: source.description
        });
        
        onApply(payload);
        setAutoApplying(false);
      }, 500);
    }
  }, [open, autoApply, normalizedDefaults, normalizedInitial, onApply, autoApplying]);

  const handleApply = () => {
    const latNumber = Number(latitude);
    const lngNumber = Number(longitude);

    if (Number.isNaN(latNumber) || Number.isNaN(lngNumber)) {
      setError('Latitude and longitude must be valid numbers.');
      return;
    }

    if (latNumber > 90 || latNumber < -90 || lngNumber > 180 || lngNumber < -180) {
      setError('Latitude must be between -90 and 90, longitude between -180 and 180.');
      return;
    }

    const payload = normalizeGeotagMetadata({
      latitude: latNumber,
      longitude: lngNumber,
      keywords: splitKeywords(keywordsInput),
      description
    });

    onApply(payload);
  };

  const handleUseDefaults = () => {
    if (normalizedDefaults.latitude == null || normalizedDefaults.longitude == null) {
      setError('No stored coordinates detected for this profile. Enter them manually.');
      return;
    }

    setLatitude(toNumberOrEmpty(normalizedDefaults.latitude));
    setLongitude(toNumberOrEmpty(normalizedDefaults.longitude));
    if (!keywordsInput) {
      setKeywordsInput(keywordListToInput(normalizedDefaults.keywords));
    }
    if (!description) {
      setDescription(normalizedDefaults.description || '');
    }
    setError('');
  };

  const handleGenerateDescription = async () => {
    if (!onGenerateDescription) return;
    try {
      const generated = await onGenerateDescription({
        latitude: latitude ? Number(latitude) : normalizedDefaults.latitude,
        longitude: longitude ? Number(longitude) : normalizedDefaults.longitude,
        keywords: splitKeywords(keywordsInput)
      });
      if (generated) {
        setDescription(generated);
        setError('');
      }
    } catch (generateError) {
      setError(generateError.message || 'Failed to generate description. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Geotag Image</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {autoApplying ? (
            <Alert severity="success" icon={false} sx={{ fontSize: 13 }}>
              ✓ Auto-applying geotag with all required fields...
            </Alert>
          ) : (
            <Alert severity="info" icon={false} sx={{ fontSize: 13 }}>
              Add Google-friendly location metadata to improve local visibility. You can use stored profile coordinates or enter them manually.
            </Alert>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Latitude"
              value={latitude}
              type="number"
              onChange={(event) => setLatitude(event.target.value)}
              inputProps={{ step: 'any' }}
              fullWidth
              required
            />
            <TextField
              label="Longitude"
              value={longitude}
              type="number"
              onChange={(event) => setLongitude(event.target.value)}
              inputProps={{ step: 'any' }}
              fullWidth
              required
            />
          </Stack>

          <Button
            variant="outlined"
            startIcon={<MyLocationIcon />}
            onClick={handleUseDefaults}
            disabled={normalizedDefaults.latitude == null || normalizedDefaults.longitude == null}
          >
            Auto-fill from business profile
          </Button>

          <Divider sx={{ my: 1.5 }} />

          <TextField
            label="Keywords"
            value={keywordsInput}
            onChange={(event) => setKeywordsInput(event.target.value)}
            helperText="Separate keywords with commas or new lines."
            multiline
            minRows={2}
            fullWidth
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              Use relevant service keywords and local terms (e.g. neighbourhood, city). Supports JPG and PNG images for AI descriptions.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleGenerateDescription}
              disabled={generatingDescription}
            >
              {generatingDescription ? 'Generating…' : 'Generate description'}
            </Button>
          </Stack>

          <TextField
            label="Image description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            helperText="Add 1-2 sentences describing what customers see."
            multiline
            minRows={3}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleApply}>
          Apply Geotag
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GeotagDialog;
